import { Settings, GalleryItem, ShoppingGalleryItem, CoverItem } from 'sofa-reborn-react';

import {
	ProductService,
	KompisDefaultFields,
	CheckoutAvailabilityService,
} from '@inter-ikea-kompis/services';
import {
	IProductItem,
	IProduct,
	IShoppingProduct,
	IShoppingItem,
	IStoreAvailability,
} from '@inter-ikea-kompis/types';
import { ProductOptionalFieldNameEnum } from '@inter-ikea-kompis/enums';

import { plannerConfigService, Config, FetchService, localeService } from '../../../services';

export type IDataOptions = {
	settings: Settings;
	cmsConfigVersion: string;
	sofaRange: string;
	disableBulkFetch?: boolean;
	usePersistedConfig?: boolean;
};

/**
 * Data handler for all thunks fetch feature.
 */
export default class DataHandler {
	public sofaFields: ProductOptionalFieldNameEnum[] = [
		...KompisDefaultFields,
		ProductOptionalFieldNameEnum.careInstruction,
		ProductOptionalFieldNameEnum.customerMaterial,
		ProductOptionalFieldNameEnum.complianceInformation,
	];

	/**
	 * Gets data async parallell.
	 *
	 * @param options IDataOptions.
	 * @returns Config and Product data.
	 */
	public async getData(options: IDataOptions): Promise<[unknown, IProduct[]]> {
		if (options.disableBulkFetch) {
			const config = await this.getConfig(options);
			const fetchedProducts = await this.getFetchedProductsWithConfig(options, config as Config);
			return [config, fetchedProducts];
		} else {
			return (await Promise.all([this.getConfig(options), this.getFetchedProducts(options)])) as [
				unknown,
				IProduct[]
			];
		}
	}

	/**
	 * Gets products.
	 *
	 * @param settings Settings.
	 * @param productItems IProductsItems.
	 * @returns Product data.
	 */
	public async getProductFromService(
		settings: Settings,
		productItems: IProductItem[]
	): Promise<IProduct[]> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const products = await productService.getProducts(productItems, this.sofaFields);
		return products.filter((product) => product.valid);
	}

	/**
	 * Gets cover only items.
	 *
	 * @param settings Settings.
	 * @param covers Current covers.
	 * @returns Cover items.
	 */
	public async getCoverOnlyItems(settings: Settings, covers: CoverItem[]): Promise<CoverItem[]> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const items = covers.map((cover) => {
			return { id: cover.articleId, quantity: 1, type: 'ART' };
		});

		const coverProducts = await productService.getShoppingProducts(items, KompisDefaultFields);

		const coverOnlyItems = covers.filter((cover) => {
			return coverProducts.some((coverProduct) => {
				return (
					(cover.articleId === coverProduct.product.content?.itemNoGlobal ||
						cover.articleId === coverProduct.product.content?.itemNoLocal) &&
					coverProduct.product.valid &&
					coverProduct.product.content?.mainTypeCode === '00062'
				);
			});
		});

		return coverOnlyItems;
	}

	/**
	 * Get shopping gallery items.
	 *
	 * @param settings Settings.
	 * @param galleryItems GalleryItems.
	 * @param storeId Store Identity.
	 * @returns Shopping gallery products.
	 */
	public async getShoppingGalleryItems(
		settings: Settings,
		galleryItems: GalleryItem[],
		storeId: string | undefined
	): Promise<ShoppingGalleryItem[]> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});
		const checkoutAvailabilityService = new CheckoutAvailabilityService({
			locale: settings.locale,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const hasStoreAvailability =
			!!storeId &&
			localeService.getLocalizationSetting('storeAvailabilityDataSource') !== 'disabled';

		const galleryShoppingItems: ShoppingGalleryItem[] = [];
		await galleryItems.reduce(async (_promise, item) => {
			const shoppingProducts = await productService.getShoppingProducts(
				[item.productItem as IShoppingItem],
				this.sofaFields
			);
			this.addGalleryShoppingItem(
				{ checkoutAvailabilityService, storeId, shoppingProducts, item, hasStoreAvailability },
				galleryShoppingItems
			);
		}, Promise.resolve());
		return galleryShoppingItems;
	}

	private async addGalleryShoppingItem(
		options: {
			checkoutAvailabilityService: CheckoutAvailabilityService;
			storeId: string | undefined;
			shoppingProducts: IShoppingProduct[];
			item: GalleryItem;
			hasStoreAvailability: boolean;
		},
		galleryShoppingItems: ShoppingGalleryItem[]
	): Promise<void> {
		const { shoppingProducts } = options;
		if (shoppingProducts && shoppingProducts.length > 0) {
			if (
				shoppingProducts[0].product.content &&
				!shoppingProducts[0].product.content.itemNoGlobal
			) {
				shoppingProducts[0].product.content.itemNoGlobal =
					shoppingProducts[0].product.content.itemNoLocal;
			}

			let storeAvailability: IStoreAvailability | null = null;
			if (options.hasStoreAvailability) {
				storeAvailability = await this.getStoreAvailability(options);
			}

			galleryShoppingItems.push({
				shoppingProduct: shoppingProducts[0],
				functionIcons: options.item.functionIcons,
				storeAvailability,
			});
		}
	}

	private async getStoreAvailability(options: {
		checkoutAvailabilityService: CheckoutAvailabilityService;
		storeId: string | undefined;
		shoppingProducts: IShoppingProduct[];
		item: GalleryItem;
	}): Promise<IStoreAvailability | null> {
		if (options.storeId && options.shoppingProducts[0].product.content) {
			const storeAvailabilities = await options.checkoutAvailabilityService.getStoreAvailabilities(
				options.storeId,
				[
					{
						id:
							options.shoppingProducts[0].product.content.itemNoLocal ||
							options.shoppingProducts[0].product.content.itemNoGlobal ||
							'',
						type: options.item.productItem.type,
					},
				]
			);
			return storeAvailabilities ? storeAvailabilities[0] : null;
		}
		return null;
	}

	private downloadCMSConfigurationFile(config: unknown) {
		const getFile = window.confirm(
			'Would you like to download cms-config.json?' +
				' You need to put your cms-config.json in your packages/sofa-reborn-react/webapp folder,' +
				' for it to work with persisted config and reload the page.'
		);
		if (getFile) {
			const a = document.createElement('a');
			a.href = URL.createObjectURL(
				new Blob([JSON.stringify(config, null, '\t')], { type: 'text/plain' })
			);
			a.download = 'cms-config.json';
			a.click();
		}
	}

	private async getPersistedConfig(options: IDataOptions): Promise<unknown> {
		try {
			const cmsConfigJSON = await FetchService.json(`${window.location.origin}/cms-config.json`);
			if (cmsConfigJSON) {
				return cmsConfigJSON;
			}
		} catch (e) {}
		const configService = new plannerConfigService({ cmsConfigVersion: options.cmsConfigVersion });
		const config = await configService.getConfig(options.sofaRange);
		this.downloadCMSConfigurationFile(config);
		return config;
	}

	private getConfig(options: IDataOptions) {
		if (options.usePersistedConfig && options.settings.environment !== 'production') {
			return this.getPersistedConfig(options);
		}
		const configService = new plannerConfigService({ cmsConfigVersion: options.cmsConfigVersion });
		return configService.getConfig(options.sofaRange);
	}

	private async getFetchedProducts(options: IDataOptions): Promise<IProduct[]> {
		const { settings } = options;
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
			dexfApplicationId: options.sofaRange,
		});
		const applicationProducts = await productService.getApplicationProducts(this.sofaFields);
		return applicationProducts.filter((product) => product.valid);
	}

	private async getFetchedProductsWithConfig(
		options: IDataOptions,
		config: Config
	): Promise<IProduct[]> {
		const { settings } = options;
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
			dexfApplicationId: options.sofaRange,
		});
		const configService = new plannerConfigService({});
		const productItems = configService.parseOutIds(config);
		const items: IProductItem[] = [];
		productItems.forEach((productItem) => {
			const [type, id] = productItem.split('-');
			items.push({ id, type });
		});
		const applicationProducts = await productService.getProducts(items, this.sofaFields);
		return applicationProducts.filter((product) => product.valid);
	}
}
